// Drawer content lives at ProjectRoomPage level so every tab shares one
// right-side drawer (Captain 2026-04-23: "the right drawer is our canon
// place where things open when content is clicked now"). Each tab has
// a default "resident expert" seat; a tab may override drawer content
// imperatively (e.g. clicking a model in the Models tab opens a
// model-chat in the same drawer).

import type { CouncilSeat } from "../../api/client";
import type { TabId } from "./TabBar";

export type DrawerContent =
  | { kind: "team_chat" }
  | { kind: "seat"; seat: CouncilSeat }
  | {
      kind: "model";
      modelId: string;
      modelName: string;
      description?: string;
    }
  | {
      kind: "thread";
      threadId: number;
      title: string;
      threadKind: "group" | "dm";
    };

export const TAB_DEFAULT_SEAT: Record<TabId, CouncilSeat> = {
  // A33 (canon update 2026-04-26): Sandbox is the default tab. Vela is
  // the natural resident — she narrates the working agent's progress.
  sandbox: "vela",
  operator: "vela",
  chat: "vela",
  // Road absorbs the Ledger's curated+raw lists under the lane; Shore
  // becomes the drawer default because the curated entries are her
  // output surface.
  road: "shore",
  files: "compass",
  telemetry: "shore",
  personas: "pearl",
  // Recipes are Captain's portable-team shelf; Pearl helps tune card
  // content so she's the natural drawer seat.
  recipes: "pearl",
  // A22 receipts are Shore's evidence read; she's the natural seat.
  receipts: "shore",
  // A25 newsfeed mixes Maris (methodology + industry framing) with
  // Compass (cost/provider). Maris tends to be the better default
  // because she frames findings against the methodology.
  newsfeed: "maris",
  models: "shore",
  usage: "compass",
  settings: "compass",
  status: "compass",
};

interface SeatMeta {
  displayName: string;
  role: string;
  greeting: string;
}

export const SEAT_META: Record<CouncilSeat, SeatMeta> = {
  pearl: {
    displayName: "Pearl",
    role: "Agent Designer",
    greeting:
      "Hi. I'm Pearl. Tell me what you want this agent to do and we'll shape a card together — display name, role, voice, model pick.",
  },
  maris: {
    displayName: "Maris",
    role: "Waytide Methodologist",
    greeting:
      "Maris here. I keep the method honest — Line shape, gates, watchers, scoring lenses. Ask what belongs where.",
  },
  vela: {
    displayName: "Vela",
    role: "Project Manager",
    greeting:
      "Vela here. I coordinate Workspaces — Line planning, watcher orchestration, morning digests. Tell me what you want to ship and I'll frame it.",
  },
  shore: {
    displayName: "Shore",
    role: "Appraiser",
    greeting:
      "Shore here. I read evidence end-to-end. Ask about a persona or model score, or pick a model to talk to and I'll help you compare.",
  },
  compass: {
    displayName: "Compass",
    role: "Usage & Infrastructure",
    greeting:
      "Compass here. I watch usage, limits, and provider keys. Ask about budgets, caps, or why something isn't routing.",
  },
  coral: {
    displayName: "Coral",
    role: "Creator Experience",
    greeting:
      "Coral here. Visual polish, micro-copy, and creator flow. Ping me when a surface feels off.",
  },
};
